import type { ReactNode } from 'react';
import React, {
  Children,
  useContext,
  useEffect,
  useRef,
  useImperativeHandle,
  forwardRef,
} from 'react';
import { classnames } from 'Utils/classnames/classnames.utils';
import { forceCheck } from 'react-lazyload';
import { throttle } from 'lodash';
import { FocusOn } from 'react-focus-on';
import { COLORS } from 'Components/utils/color/colors';
import { ToastContext } from 'Context/ToastContext';
import { BodyLocker } from 'Components/frameworks/body-locker/BodyLocker';
import Portal from 'Components/frameworks/portal/Portal';
import { LayerHeader } from './layer-header/LayerHeader';
import type { LayerHeaderButtonProps } from './layer-header/layer-header-button/LayerHeaderButton';
import './layer-header/layer-header-button/LayerHeaderButton';
type Props = {
  ariaLabel?: string;
  ariaLabelledby?: string;
  bgColor?: LayoutGreysColors | 'white';
  buttonLeft?: LayerHeaderButtonProps | null;
  buttonRight?: LayerHeaderButtonProps | null;
  children?: ReactNode;
  customHeader?: {
    content: ReactNode;
    height: number;
  };
  className?: string;
  closeLabel?: string;
  dataTestId?: string;
  enterAnimation?: 'animate-slideInBottom' | 'ltr:animate-slideInRight rtl:animate-slideInLeft';
  externalFocusTrapRefs?: Array<ReactRef>;
  hasAnimateEnter?: boolean;
  hasContainer?: boolean;
  hasContainerHeader?: boolean;
  hasNoFocusTrap?: boolean;
  header?: ReactNode;
  onClose?: (e?: React.SyntheticEvent) => void;
  portalName?: string;
  theme?: 'default';
};
export const LAYER_HEADER_HEIGHT = 60;
const onScroll = throttle(() => {
  forceCheck();
}, 250);

const LayerComponent = (
  {
    ariaLabel,
    ariaLabelledby,
    bgColor = COLORS.WHITE,
    buttonLeft,
    buttonRight,
    customHeader,
    children,
    className,
    closeLabel,
    dataTestId = 'Layer',
    enterAnimation = 'ltr:animate-slideInRight rtl:animate-slideInLeft',
    externalFocusTrapRefs = [],
    hasAnimateEnter,
    hasContainer,
    hasContainerHeader,
    hasNoFocusTrap,
    header,
    onClose,
    portalName,
    theme = 'default',
  }: Props,
  layerRef,
) => {
  const { ref } = useContext(ToastContext);
  const layerBody = useRef<null | HTMLElement>(null);
  useImperativeHandle(layerRef, () => layerBody.current, [layerBody]);
  useEffect(() => {
    layerBody.current?.addEventListener('scroll', onScroll);
    return () => {
      layerBody.current?.removeEventListener('scroll', onScroll);
    };
  }, []);

  if (!Children.count(children)) {
    return null;
  }

  const hasHeader = buttonLeft || buttonRight || header;

  const getStyle = () => {
    return hasHeader || (!hasHeader && customHeader)
      ? {
          paddingTop: customHeader ? customHeader.height : LAYER_HEADER_HEIGHT,
        }
      : {};
  };

  const shards = ref ? [ref, ...externalFocusTrapRefs] : externalFocusTrapRefs;
  return (
    <Portal name={portalName}>
      <FocusOn
        allowPinchZoom
        enabled={!hasNoFocusTrap}
        noIsolation
        onEscapeKey={onClose as unknown as ((event: Event) => void) | undefined}
        shards={shards.filter(Boolean)}
      >
        <BodyLocker isLocked>
          <aside
            aria-label={ariaLabelledby ? null : ariaLabel}
            aria-labelledby={ariaLabelledby}
            role="dialog"
          >
            <div
              className={classnames(
                `animation-duration-500 bg-${bgColor}`,
                'fixed inset-0 w-full',
                {
                  [enterAnimation]: hasAnimateEnter,
                },
                className,
              )}
              data-testid={dataTestId}
              role="document"
            >
              {hasHeader && (
                <LayerHeader
                  buttonLeft={buttonLeft}
                  buttonRight={buttonRight}
                  closeLabel={[closeLabel, ariaLabel].join(' ').trim()}
                  hasContainer={hasContainerHeader}
                  height={LAYER_HEADER_HEIGHT}
                  onClose={onClose}
                  theme={theme}
                >
                  {header}
                </LayerHeader>
              )}
              {!hasHeader && customHeader && customHeader.content}
              <div
                className={classnames('scroll-y z-0 h-full transition-all duration-250 ease-in', {
                  'px-20': hasContainer,
                })}
                data-testid="Layer-body"
                ref={layerBody as React.MutableRefObject<HTMLDivElement | null>}
                style={getStyle()}
              >
                <div
                  className={classnames('h-full min-h-full', {
                    'mx-auto w-full max-w-site': hasContainer,
                  })}
                  data-testid="Layer-content"
                >
                  {children}
                </div>
              </div>
            </div>
          </aside>
        </BodyLocker>
      </FocusOn>
    </Portal>
  );
};

export const Layer = forwardRef(LayerComponent);
